import { useMemo, useState } from 'react';

import { Badge, Button, CopyButton, EmptyState, Switch, Tabs } from '../common';
import { useAttributeSet } from '../../hooks/useAttributeSet';
import './attributes.css';

const TABS = [
  { id: 'attributes', label: 'Attributes' },
  { id: 'raw', label: 'Raw' },
  { id: 'json', label: 'JSON' },
  { id: 'prompt', label: 'Prompt' },
];

const STATE_LABELS = {
  ok: 'valid',
  fuzzy: 'fuzzy',
  unmatched: 'unmatched',
  missing: 'missing',
};

/**
 * The inspector: the structured output, pinned beside the conversation.
 *
 * It reports four states rather than a confidence score, because the model emits
 * no probabilities — the upstream `confidence` field is hardcoded to 1.0. What
 * matters is whether a value is in the vocabulary (`valid`), was snapped to it
 * (`fuzzy` — worth a look), fell outside it (`unmatched`), or never arrived
 * (`missing`). A fake confidence bar would imply information nobody has.
 */
export function AttributePanel({ attributes, message, sentPrompt, onClose = null }) {
  const [tab, setTab] = useState('attributes');
  const [hideDefaults, setHideDefaults] = useState(false);
  const [stateFilter, setStateFilter] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const setId = attributes?.attribute_set;
  const { attributeSet } = useAttributeSet(setId);

  const definitions = useMemo(() => {
    const map = new Map();
    (attributeSet?.attributes || []).forEach((attribute) => map.set(attribute.name, attribute));
    return map;
  }, [attributeSet]);

  const predictions = useMemo(() => attributes?.predictions || [], [attributes]);
  const counts = attributes?.counts || { ok: 0, fuzzy: 0, unmatched: 0, missing: 0 };

  const visible = useMemo(() => {
    let rows = predictions;
    if (stateFilter) rows = rows.filter((row) => row.state === stateFilter);
    if (hideDefaults) {
      rows = rows.filter((row) => {
        const definition = definitions.get(row.attr_name);
        return !definition?.most_common || definition.most_common !== row.value;
      });
    }
    return rows;
  }, [predictions, stateFilter, hideDefaults, definitions]);

  const grouped = useMemo(() => {
    const groups = new Map();
    visible.forEach((row) => {
      const key = row.group || 'Other';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(row);
    });
    return [...groups.entries()];
  }, [visible]);

  if (!attributes) {
    return (
      <aside className="inspector">
        <div className="inspector__tabs">
          <Tabs tabs={[TABS[0]]} active="attributes" onChange={() => {}} />
        </div>
        <EmptyState glyph="▤" title="No structured output yet">
          Send a road image with attribute extraction on, and the coded attributes appear here —
          grouped, with their validity state.
        </EmptyState>
      </aside>
    );
  }

  return (
    <aside className="inspector">
      <div className="inspector__tabs">
        <Tabs tabs={TABS} active={tab} onChange={setTab} />
      </div>

      {tab === 'attributes' && (
        <>
          <div className="attr-stats">
            {[
              { key: 'ok', label: 'valid', tone: 'ok' },
              { key: 'fuzzy', label: 'fuzzy', tone: 'fuzzy' },
              { key: 'unmatched', label: 'unmatched', tone: 'warn' },
              { key: 'missing', label: 'missing', tone: 'miss' },
            ].map((stat) => (
              <button
                key={stat.key}
                type="button"
                className={[
                  'attr-stat',
                  `attr-stat--${stat.tone}`,
                  'attr-stat--interactive',
                  stateFilter === stat.key && 'attr-stat--selected',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => setStateFilter(stateFilter === stat.key ? null : stat.key)}
                title={`Show only ${stat.label} attributes`}
              >
                <div className="attr-stat__value">{counts[stat.key] ?? 0}</div>
                <div className="attr-stat__label">{stat.label}</div>
              </button>
            ))}
          </div>

          <div className="inspector__toolbar">
            <Switch
              checked={hideDefaults}
              onChange={setHideDefaults}
              label="Hide typical values"
              title="Hide attributes whose value equals the most common value for that attribute"
            />
            <span style={{ marginLeft: 'auto' }}>
              {visible.length} of {predictions.length}
              {setId ? ` · ${setId}` : ''}
            </span>
            {stateFilter && (
              <Button size="sm" variant="ghost" onClick={() => setStateFilter(null)}>
                Clear filter
              </Button>
            )}
          </div>

          <div className="inspector__body">
            {grouped.length === 0 ? (
              <EmptyState glyph="⌕" title="Nothing matches">
                No attributes match the current filter.
              </EmptyState>
            ) : (
              grouped.map(([group, rows]) => (
                <div key={group}>
                  <div className="attr-group">
                    <span className="section-label">{group}</span>
                    <span className="xs subtle">{rows.length}</span>
                  </div>
                  {rows.map((row) => (
                    <AttributeRow
                      key={row.attr_name}
                      row={row}
                      definition={definitions.get(row.attr_name)}
                      expanded={expanded === row.attr_name}
                      onToggle={() =>
                        setExpanded(expanded === row.attr_name ? null : row.attr_name)
                      }
                    />
                  ))}
                </div>
              ))
            )}
          </div>

          <div className="inspector__foot">
            <CopyButton value={attributes.predictions} label="Copy JSON" size="md" />
            <CopyButton
              value={toTsv(predictions)}
              label="Copy as table"
              size="md"
              title="Tab-separated, ready to paste into a spreadsheet"
            />
            {onClose && (
              <Button variant="ghost" onClick={onClose} style={{ marginLeft: 'auto' }}>
                Close
              </Button>
            )}
          </div>
        </>
      )}

      {tab === 'raw' && (
        <div className="inspector__body">
          <div className="inspector__raw">{message?.raw_response || '(no response)'}</div>
        </div>
      )}

      {tab === 'json' && (
        <>
          <div className="inspector__body">
            <div className="inspector__raw">{JSON.stringify(attributes, null, 2)}</div>
          </div>
          <div className="inspector__foot">
            <CopyButton value={attributes} label="Copy JSON" size="md" />
          </div>
        </>
      )}

      {tab === 'prompt' && (
        <>
          <div className="inspector__toolbar">
            <Badge>{sentPrompt ? `${sentPrompt.length} characters` : 'no prompt recorded'}</Badge>
            <span>this is the exact text sent to the model</span>
          </div>
          <div className="inspector__body">
            <div className="inspector__raw">{sentPrompt || '(not recorded for this message)'}</div>
          </div>
          {sentPrompt && (
            <div className="inspector__foot">
              <CopyButton value={sentPrompt} label="Copy prompt" size="md" />
            </div>
          )}
        </>
      )}
    </aside>
  );
}

function AttributeRow({ row, definition, expanded, onToggle }) {
  const valueClass = row.state === 'ok' ? '' : `attr-row__value--${row.state}`;
  const label = row.state === 'missing' ? '— no answer' : row.value || '—';

  return (
    <>
      <button type="button" className="attr-row" onClick={onToggle}>
        <span className={`attr-row__dot attr-row__dot--${row.state}`} aria-hidden="true" />
        <span className="attr-row__name" title={row.attr_name}>
          {row.attr_name}
        </span>
        <span className={`attr-row__value ${valueClass}`}>{label}</span>
      </button>

      {expanded && (
        <div className="attr-detail">
          <div className="attr-detail__title">{row.attr_name}</div>
          <div className="muted">
            {definition?.description || 'No description in the attribute set.'}
          </div>
          <div className="row xs" style={{ marginTop: 7, gap: 10, flexWrap: 'wrap' }}>
            <span>
              State: <strong>{STATE_LABELS[row.state]}</strong>
            </span>
            <span>
              Class index: <strong>{row.index >= 0 ? row.index : '—'}</strong>
            </span>
            {definition?.most_common && <span>Typical: {definition.most_common}</span>}
          </div>
          {row.state !== 'ok' && row.raw && (
            <div className="xs" style={{ marginTop: 6, color: 'var(--state-warn)' }}>
              Model emitted: “{row.raw}”
            </div>
          )}
          {definition?.values?.length > 0 && (
            <div className="attr-detail__values">
              {definition.values.map((value) => (
                <span
                  key={value}
                  className={`attr-detail__value ${value === row.value ? 'attr-detail__value--chosen' : ''}`}
                >
                  {value}
                </span>
              ))}
            </div>
          )}
          {definition?.tips?.length > 0 && (
            <div className="xs subtle" style={{ marginTop: 7 }}>
              {definition.tips.map((tip) => (
                <div key={tip}>• {tip}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

function toTsv(predictions) {
  return ['Attribute\tValue\tState\tIndex']
    .concat(predictions.map((row) => `${row.attr_name}\t${row.value}\t${row.state}\t${row.index}`))
    .join('\n');
}
